import { useState, type ChangeEvent, useEffect } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useDashIntl, useDashMessages } from 'intl/intl.hooks'
import {
  type CodeLookupsValidationErrors,
  type CodeLookups,
  type CodeLookupsCodeType,
} from 'domains/collections/components/collections-form/components/code-lookup-matcher/utils/CodeLookupMatcher.types'
import {
  parseCodeLookups,
  validateCodeLookups,
} from 'domains/collections/components/collections-form/components/code-lookup-matcher/utils/CodeLookupMatcher.utils'
import { type DashMessages } from 'intl/intl.types'
import { normalizeProductCode } from 'utils/product-codes/normalizeProductCodes'
import { useDomainMessages } from 'domains/collections/utils/domain/intl'
import { useCreateCodeLookupBatchMutation, CodeLookupTypeEnum } from 'domains/collections/api'

interface UseCodeLookupMatcherArgs {
  onCodeLookupBatchCreation: (codeLookupBatchId: string) => void
  codeType: CodeLookupsCodeType
  retailerId?: string
  localeCode?: string
}

export const useCodeLookupMatcher = ({
  onCodeLookupBatchCreation,
  codeType,
  retailerId,
  localeCode,
}: UseCodeLookupMatcherArgs) => {
  const intl = useDashIntl()

  const [triggerMutation, { data, loading, error }] = useCreateCodeLookupBatchMutation()

  const catalogCodeLookupBatchId = data?.catalogCodeLookupBatchCreate?.catalogCodeLookupBatch?.id

  useEffect(() => {
    if (catalogCodeLookupBatchId) {
      onCodeLookupBatchCreation(catalogCodeLookupBatchId)
    }
  }, [catalogCodeLookupBatchId, onCodeLookupBatchCreation])

  const [codeLookups, setCodeLookups] = useState<CodeLookups>([])
  const [codeValidationErrors, setCodeValidationErrors] = useState<CodeLookupsValidationErrors>({})

  const handleTextAreaChange = ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
    const parsedCodeLookups = parseCodeLookups(value)
    const validationErrors = validateCodeLookups(parsedCodeLookups)

    setCodeLookups(parsedCodeLookups)
    setCodeValidationErrors(validationErrors)
  }

  const handleSubmit = () => {
    const codeLookupType = codeType === 'UPC' ? CodeLookupTypeEnum.Upc : CodeLookupTypeEnum.Rrc
    triggerMutation({
      variables: {
        codeLookupsData: codeLookups.map(codeLookup => {
          const normalizedCode =
            codeLookupType === CodeLookupTypeEnum.Upc
              ? normalizeProductCode(codeLookup)
              : codeLookup

          return { code: normalizedCode, codeLookupType, retailerId, localeCode }
        }),
      },
    }).then(results => {
      onCodeLookupBatchCreation(results.data.catalogCodeLookupBatchCreate.catalogCodeLookupBatch.id)
    })
  }
  useDomainMessages
  const messages = useDomainMessages({
    headerMessage: 'collectionsDomain.addByUpc.header',
    descriptionMessage: 'collectionsDomain.addByUpc.description',
    textAreaPlaceholderMessage: 'collectionsDomain.addByUpc.textAreaPlaceholder',
    cancelButtonMessage: 'collectionsDomain.addByUpc.cancel',
    submitButtonMessage: 'collectionsDomain.addByUpc.submitAction',
    submissionError: 'collectionsDomain.addByUpc.submissionError',
  })

  const validationErrorMessages = Object.entries(codeValidationErrors).map(([errorKey, value]) => {
    if (!value) return null

    return intl.formatMessage({
      id: `components.codeLookupMatcher.validationError.${errorKey}` as DashMessages,
    })
  })

  const submitDisabled = codeLookups.length === 0 || loading || !isEmpty(codeValidationErrors)

  return {
    error,
    handleSubmit: handleSubmit,
    handleTextAreaChange,
    messages,
    validationErrorMessages,
    submitDisabled,
    codeLookups,
  }
}
